import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef, HTMLAttributes } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A bottom sheet — same Radix primitive as `Dialog` / `Sheet` (focus trap,
 * scroll lock, Esc, portal) but it slides **up from the bottom** and always
 * leaves a strip of backdrop visible at the top. This is the phone-native shape
 * and the pattern a future mobile client mirrors — use it for user-facing action
 * panels (compose, pick, report). The admin tool keeps `Sheet` (right-anchored).
 *
 * ```tsx
 * <Drawer open={open} onOpenChange={setOpen}>
 *   <DrawerContent>
 *     <DrawerHeader><DrawerTitle>…</DrawerTitle></DrawerHeader>
 *     …body…
 *     <DrawerFooter>…</DrawerFooter>
 *   </DrawerContent>
 * </Drawer>
 * ```
 */
export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;

export const DrawerContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="dialog-overlay fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'drawer-content fixed inset-x-0 bottom-0 z-50 mx-auto flex max-w-lg flex-col',
        'max-h-[calc(100dvh-3.5rem)] rounded-t-tile border-2 border-b-0 border-ink',
        'bg-surface shadow-xl focus:outline-none',
        className,
      )}
      {...props}
    >
      <div
        className="mx-auto mt-2.5 h-1.5 w-10 shrink-0 rounded-pill bg-hairline-strong"
        aria-hidden
      />
      <div className="flex-1 overflow-y-auto px-6 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        {children}
      </div>
      <DialogPrimitive.Close
        className="absolute right-4 top-3.5 inline-flex size-8 items-center justify-center rounded-full text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:outline-2 focus-visible:outline-ink"
        aria-label="Close"
      >
        <X className="size-4" strokeWidth={2} />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DrawerContent.displayName = 'DrawerContent';

export function DrawerHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

export function DrawerFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const DrawerTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('font-display text-xl font-semibold text-ink', className)}
    {...props}
  />
));
DrawerTitle.displayName = 'DrawerTitle';

export const DrawerDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-ink-soft', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';
